// src/ui/screens/passport.ts
// THE PASSPORT SURFACE — practice evidence as it reaches the Athlete Passport. Read-only.
//
// This is the `passport-bridge` lane: `passport-events` and the `passport-timeline` projection.
//
// TWO THINGS THIS SCREEN DOES NOT DO:
//
//   · It does not call `scout-evidence`. That is a different audience, on a different path, chosen
//     by the backend precisely so an athlete/coach screen can never widen into a scout screen. A
//     client that renders both is one missing `if` away from serving a child's unreviewed camera
//     estimate to a selector. The function exists in api/practice.ts, unused and labelled.
//   · It does not invent a metric. `shareable_clips` comes back `[]` on every event today, with
//     `not_available` explaining why no table records a share approval for a practice clip. Both are
//     rendered. The empty array is not hidden and the explanation is not paraphrased.
//
// `read_only: true` for a guardian is rendered as a fact, because the ROLE selects the surface — not
// anything this client sends.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal } from '../../api/client';
import { passportEvents, passportTimeline } from '../../api/practice';
import { getState } from '../state';

export async function passportScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  if (!st.athlete) {
    mount(root, section('Passport',
      empty('No athlete selected. Pick one on the Coach screen or the Parent screen — this app only '
        + 'ever uses an athlete id the server handed it.')));
    return;
  }
  const athleteId = st.athlete.athleteId;
  mount(root, spinner('Loading Passport evidence…'));

  const [events, timeline] = await Promise.all([
    passportEvents(athleteId, 25),
    passportTimeline(athleteId),
  ]);

  const out = el('div');

  if (isRefusal(events)) {
    out.append(section('Practice evidence', refusalView(events, { onRetry: rerender })));
  } else {
    const v = events.value;
    out.append(section('Practice evidence',
      field('Your role', v.role),
      field('Read only', v.read_only ? 'yes' : 'no'),
      v.events.length
        ? el('div', { class: 'list' }, ...v.events.map((e) => el('div', { class: 'row-item' },
            e.session_id && field('Session', e.session_id),
            e.at && field('At', e.at),
            // Rendered as the fact it is: today this is always empty, and the server says why.
            field('Shareable clips', (e.shareable_clips ?? []).length),
            e.not_available !== undefined && e.not_available !== null
              && el('p', { class: 'note' },
                typeof e.not_available === 'string' ? e.not_available : JSON.stringify(e.not_available)),
          )))
        : empty('No practice sessions have reached the Passport for this athlete yet.'),
    ));
  }

  if (isRefusal(timeline)) {
    out.append(section('Timeline', refusalView(timeline, { onRetry: rerender })));
  } else {
    const entries = timeline.value.timeline ?? [];
    out.append(section('Timeline',
      el('p', { class: 'note' },
        'These are the exact entries the Passport timeline will carry once the two-line handoff '
        + 'lands on the Passport lane. Reads run under your own database permissions — the database '
        + 'decides the audience, not this app.'),
      entries.length
        ? el('div', { class: 'list' }, ...entries.map(timelineRow))
        : empty('No timeline entries for this athlete.'),
    ));
  }
  mount(root, out);
}

/**
 * One timeline entry.
 *
 * The projection's exact fields are the Passport lane's to define, so this renders the labelled
 * fields it knows and, for anything else, prints the entry as data rather than dropping it. Dropping
 * unknown fields on an evidence surface is how a client quietly narrows what an athlete can see.
 */
function timelineRow(entry: unknown): HTMLElement {
  const e = (entry ?? {}) as Record<string, unknown>;
  const known = ['label', 'kind', 'ts', 'at', 'id', 'ref_id'];
  const rest = Object.fromEntries(Object.entries(e).filter(([k]) => !known.includes(k)));
  return el('div', { class: 'row-item' },
    typeof e['label'] === 'string' && el('p', {}, e['label']),
    typeof e['kind'] === 'string' && field('Kind', e['kind']),
    field('At', typeof e['ts'] === 'string' ? e['ts'] : (typeof e['at'] === 'string' ? e['at'] : '—')),
    Object.keys(rest).length > 0
      && el('details', {}, el('summary', {}, 'Other fields'), el('pre', {}, JSON.stringify(rest, null, 2))),
  );
}
